import { Button } from '../../components/ui/button';
import { CompactTable } from '../../components/ui/CompactTable';

interface Column<T> {
    header: string;
    accessor: (item: T) => React.ReactNode;
    className?: string;
}

interface PagedReportTableProps<T> {
    data: T[];
    columns: Column<T>[];
    keyField: keyof T;
    total: number;
    limit: number;
    offset: number;
    loading?: boolean;
    emptyMessage?: string;
    onPageChange: (nextOffset: number) => void;
    onPageSizeChange: (nextLimit: number) => void;
}

export function PagedReportTable<T>({
    data,
    columns,
    keyField,
    total,
    limit,
    offset,
    loading = false,
    emptyMessage,
    onPageChange,
    onPageSizeChange,
}: PagedReportTableProps<T>) {
    const start = total === 0 ? 0 : offset + 1;
    const end = Math.min(offset + data.length, total);
    const canGoBack = offset > 0;
    const canGoNext = offset + limit < total;

    return (
        <div className="space-y-3">
            <div className="overflow-x-auto">
                <CompactTable
                    data={data}
                    columns={columns}
                    keyField={keyField}
                    loading={loading}
                    emptyMessage={emptyMessage}
                />
            </div>

            <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                    <span className="font-medium text-slate-800">
                        {start}-{end} of {total}
                    </span>
                    <label className="flex items-center gap-2">
                        <span className="text-xs uppercase tracking-wide text-slate-500">Page size</span>
                        <select
                            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                            value={String(limit)}
                            onChange={(event) => onPageSizeChange(Number(event.target.value))}
                        >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </label>
                </div>

                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => onPageChange(Math.max(0, offset - limit))} disabled={!canGoBack || loading}>
                        Previous
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => onPageChange(offset + limit)} disabled={!canGoNext || loading}>
                        Next
                    </Button>
                </div>
            </div>
        </div>
    );
}
